import { enqueueSnackbar } from 'notistack';
import { useMemo, useState, useEffect } from 'react';

import { Box, Stack } from '@mui/system';
import {
  Card,
  Switch,
  Select,
  MenuItem,
  useTheme,
  CardHeader,
  Typography,
  InputLabel,
  CardContent,
  FormControl,
  FormHelperText
} from '@mui/material';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useTranslate } from 'src/locales';
import { AccountRole } from 'src/services/organisation/organisation.enum';
import { OrganizationSetting } from 'src/services/organisation/organisation.types';
import { useGetEmailMessageTemplatesQuery } from 'src/services/email-sync/email-sync.service';
import { TemplateVisibility, EmailMessageTemplateType } from 'src/services/email-sync/email-sync.types';
import {
  useGetOrganizationSettingsQuery,
  useUpdateOrganizationSettingsMutation
} from 'src/services/organisation/organisation.service';

import Iconify from 'src/components/iconify';

/**
 * Controls whether an inbound applicant is automatically invited to create a JobTetris account.
 * When off, no invite record is written at all — the applicant gets a plain acknowledgement and
 * the recruiter invites them manually from the application when ready.
 */
export default function AutoInviteSetting() {

  const { t } = useTranslate();
  const theme = useTheme();

  const { organization, account } = useAuthContext();

  const { currentData: settingsData } = useGetOrganizationSettingsQuery(
    organization.id
  );

  const [updateSettings] = useUpdateOrganizationSettingsMutation();

  const [autoInviteEnabled, setAutoInviteEnabled] = useState(true);
  const [templateId, setTemplateId] = useState('');

  // Only org-wide APPLICATION_UPDATE templates can be used — the email is sent by the system on
  // the job owner's behalf, so a template private to one account would not be resolvable.
  const { currentData: templatesData } = useGetEmailMessageTemplatesQuery({
    page: 1,
    per_page: 100,
    type: EmailMessageTemplateType.APPLICATION_UPDATE
  });

  const orgWideTemplates = useMemo(
    () => (templatesData?.results ?? []).filter(
      (template) => template.visibility === TemplateVisibility.ORG_WIDE
    ),
    [templatesData]
  );

  useEffect(() => {
    if (!settingsData) return;

    const autoInviteSetting = settingsData.find(
      (setting) => setting.setting_type === OrganizationSetting.AUTO_INVITE_APPLICANTS
    );

    if (autoInviteSetting) {
      setAutoInviteEnabled(autoInviteSetting.setting_value as boolean);
    }

    const templateSetting = settingsData.find(
      (setting) => setting.setting_type === OrganizationSetting.APPLICATION_RECEIVED_TEMPLATE_ID
    );

    if (templateSetting) {
      setTemplateId((templateSetting.setting_value as string) ?? '');
    }
  }, [settingsData]);

  const saveSetting = async (settingType: OrganizationSetting, value: boolean | string) => {
    if (!settingsData) return;

    const setting = settingsData.find((item) => item.setting_type === settingType);
    if (!setting) return;

    try {
      await updateSettings({
        organizationId: organization.id,
        settings: [{ id: setting.id, value }],
      }).unwrap();

      enqueueSnackbar(t('admin-organization.settings.business_settings.update.success'), { variant: 'success' });
    } catch (e) {
      enqueueSnackbar(t('admin-organization.settings.business_settings.update.default_error'), { variant: 'error' });
    }
  };

  const onAutoInviteToggle = async (value: boolean) => {
    setAutoInviteEnabled(value);
    await saveSetting(OrganizationSetting.AUTO_INVITE_APPLICANTS, value);
  };

  const onTemplateChange = async (value: string) => {
    setTemplateId(value);
    await saveSetting(OrganizationSetting.APPLICATION_RECEIVED_TEMPLATE_ID, value);
  };

  const canEditSettings = useMemo(() => {
    if (!account) return false;
    return [AccountRole.Admin, AccountRole.Op_Manager].includes(account.org_role);
  }, [account]);

  return (
    <Box sx={{ width: '100%' }}>
      <Card
        sx={{
          width: '100%',
          position: 'relative',
          boxShadow: 0
        }}
      >
        <CardHeader
          avatar={
            <Iconify
              icon="hugeicons:user-add-01"
              sx={{
                width: 30,
                height: 30,
                color: theme.palette.primary.main,
              }}
            />
          }
          title={
            <Typography variant="h6" lineHeight={1.75}>
              {t('admin-organization.settings.auto_invite.title')}
            </Typography>
          }
        />

        <CardContent>

          <Stack direction="column" justifyContent="center" sx={{ mt: 2 }} gap={2}>

            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
              <Stack>
                <Typography variant="body2">
                  {t('admin-organization.settings.auto_invite.description')}
                </Typography>
                <FormHelperText sx={{ mt: 0 }}>
                  {t('admin-organization.settings.auto_invite.helper')}
                </FormHelperText>
              </Stack>
              <Switch
                checked={autoInviteEnabled}
                disabled={!canEditSettings}
                onChange={(e) => onAutoInviteToggle(e.target.checked)}
              />
            </Stack>

            {!autoInviteEnabled && (
              <Stack gap={1}>
                <FormControl fullWidth size="small" disabled={!canEditSettings}>
                  <InputLabel id="application-received-template-label">
                    {t('admin-organization.settings.auto_invite.template_label')}
                  </InputLabel>
                  <Select
                    labelId="application-received-template-label"
                    label={t('admin-organization.settings.auto_invite.template_label')}
                    value={templateId}
                    onChange={(e) => onTemplateChange(e.target.value as string)}
                  >
                    <MenuItem value="">
                      {t('admin-organization.settings.auto_invite.template_default')}
                    </MenuItem>
                    {orgWideTemplates.map((template) => (
                      <MenuItem key={template.id} value={template.id}>
                        {template.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormHelperText sx={{ mt: 0 }}>
                  {t('admin-organization.settings.auto_invite.template_helper')}
                </FormHelperText>
              </Stack>
            )}

          </Stack>

        </CardContent>
      </Card>
    </Box>
  )
}
